import { Meta, Body, UppyFile } from "@uppy/core";
import ImageThumbnailGenerator from "@uppy/thumbnail-generator";
import { isPreviewSupported as isSupportedImageFile } from "@uppy/utils";

const isSupportedVideoFile = (fileType: string | undefined) => {
  if (!fileType) return false;
  // Seems like MP4 is the only well-supported video format
  return /^[^/]+\/(mp4)$/.test(fileType);
};

const isPreviewSupported = (fileType: string | undefined) =>
  isSupportedImageFile(fileType || "") || isSupportedVideoFile(fileType);

function videoToImage(file: File | Blob, timeoutMs = 10000): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");

    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId);

      video.onloadedmetadata = null;
      video.onseeked = null;
      video.onerror = null;

      video.pause();
      video.removeAttribute("src");
      video.load();

      URL.revokeObjectURL(url);
    };

    const fail = (error: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    const succeed = (blob: Blob) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(blob);
    };

    timeoutId = setTimeout(() => {
      fail(new Error("Timed out processing video"));
    }, timeoutMs);

    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;

    video.onerror = () => {
      fail(new Error("Failed to load video"));
    };

    video.onloadedmetadata = () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) {
        video.currentTime = 0;
        return;
      }

      video.currentTime = Math.min(3, Math.max(0, video.duration - 0.1));
    };

    video.onseeked = () => {
      try {
        if (!video.videoWidth || !video.videoHeight) {
          fail(new Error("Video has no dimensions"));
          return;
        }

        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          fail(new Error("Could not get canvas context"));
          return;
        }

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
          if (!blob) {
            fail(new Error("Failed to create image blob"));
            return;
          }

          succeed(blob);
        });
      } catch (err) {
        fail(err instanceof Error ? err : new Error("Unknown error"));
      }
    };

    video.src = url;
  });
}

export class ThumbnailGenerator<M extends Meta, B extends Body> extends ImageThumbnailGenerator<
  M,
  B
> {
  async requestThumbnail(file: UppyFile<M, B>): Promise<void> {
    if (!file.isRemote && isSupportedVideoFile(file.type)) {
      if (file.data == null) throw new Error("File data is empty");
      const data = await videoToImage(file.data);
      return super.requestThumbnail({ ...file, data, type: "image/png" });
    }

    return super.requestThumbnail(file);
  }

  onFileAdded = (file: UppyFile<M, B>): void => {
    if (!file.isRemote && !file.preview && file.data && isPreviewSupported(file.type)) {
      this.addToQueue(file.id);
    }
  };
}
